import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Link, IndexLink } from "react-router";
import { Tooltip } from "react-bootstrap";
import OverlayWithToolTip from "../../../../../common/Overlay/OverlayWithToolTip";
import { RectangleSpinner } from "../../../../../common/spinners";

import {
  ArchiveButton,
  EditButton,
  DownloadButton,
  OptionsMenu,
  MessageBox,
} from "../../../../../common";
import BasicCustomContactFields from "./BasicCustomContactFields";
import { MEMBERS } from "../../../../../../utils/data/models";

import { Field } from "redux-form";
import { SelectInput } from "../../../../../common/form";

import {
  HIGH_MANAGEMENT_WITH_MANAGER,
  FINTECH,
  AGENT,
  FINANCE,
  PLAN_ADMINISTRATOR,
  HR_CLERK
} from "../../../../../../utils/Roles";
import UrlUtils from "../../../../../../utils/UrlUtils";
import userHasAccess from "../../../../../../utils/userHasAccess";
import englishTranslations from "../../../../../../components/intl/en-CA";
import frenchTranslations from "../../../../../../components/intl/fr-CA";
import {
  ACTIVE,
  IMPLEMENTATION,
  INACTIVE,
  TERMINATED,
} from "../../../enums/clientStatusTypes";
import { MANUAL, RECURRING } from "../../../enums/billingPeriodTypes";
import { FR } from "../../../../../common/enums/languages";
import { canDownload } from "../../../../../common/CanDownload";
import { FAILURE, STARTED } from "../../../../synchronization/enums/syncStatusTypes";
import { getOverlayForSync, canViewSyncStatus, isMemberEligibleForSync, isArchivedEventError } from "../../../../../common/synchronization/SynchronizationHelper";
import { MEMBER } from "../../../../synchronization/enums/syncElementTypes";
import { EMAIL_VERIFIED } from "../../../enums/memberActivationStatusTypes";
import { TED_DESJARDINS } from "../../../enums/synchronizationOptions";

const NewTooltip = (props) => {
  return <Tooltip {...props} arrowOffsetLeft={20} positionLeft={30} />;
};

NewTooltip.propTypes = {
  arrowOffsetLeft: PropTypes.string,
};

const MemberHeader = (props) => {
  const {
    firstName,
    lastName,
    memberIdentifier,
    client,
    memberId,
    isArchived,
  } = props.member;
  const { clientInTransition } = props;

  const canViewMemberCoverageByClientStatusType = () => {
    const { client } = props;

    if (client) {
      switch (client.clientStatusType) {
        case IMPLEMENTATION.value:
          return userHasAccess(AGENT) || userHasAccess([PLAN_ADMINISTRATOR], true);
        case ACTIVE.value:
          return true;
        case INACTIVE.value:
        case TERMINATED.value:
          return userHasAccess(AGENT);
      }
    }
  };

  const renderMemberHeaderWarnings = () => {
    const { member, client } = props;

    let allWarnings = [];

    if (
      (member.firstName + " " + member.lastName).toLowerCase() ===
      "non transmis"
    ) {
      allWarnings.push(
        <MessageBox key="notTransmittedWarning" className="alert-warning">
          <FormattedMessage id="members.notTransmittedWarning" />
        </MessageBox>
      );
    }
    if (
      member.countryCode !== "CA" &&
      client.clientBillingPeriodType === RECURRING.value
    ) {
      allWarnings.push(
        <MessageBox key="canadaWarning" className="alert-warning">
          <FormattedMessage id="members.onlyCanadianSyncWarning" />
        </MessageBox>
      );
    }

    return allWarnings;
  };

  const canEditMemberByClientStatusType = () => {
    const { client } = props;

    const isMemberArchived =
      props.member && props.member.isArchived && !userHasAccess(FINTECH);

    if (client) {
      switch (client.clientStatusType) {
        case IMPLEMENTATION.value:
          return !isMemberArchived && userHasAccess(FINANCE);
        case ACTIVE.value:
          return !isMemberArchived && userHasAccess([...HIGH_MANAGEMENT_WITH_MANAGER, PLAN_ADMINISTRATOR, AGENT, HR_CLERK], true);
        case INACTIVE.value:
          return !isMemberArchived && userHasAccess([...HIGH_MANAGEMENT_WITH_MANAGER, PLAN_ADMINISTRATOR, AGENT], true);
        case TERMINATED.value:
          return !isMemberArchived && userHasAccess(FINTECH);
      }
    }
  };

  const isMemberDesynced = () => {
    if (!props.isSyncReady) return false;
    if (!props.latestMemberSync) return true;
    if (props.latestMemberSync.status === STARTED) return false;
    if (props.latestMemberSync.status === FAILURE) return true;
    if (props.member && props.member["memberPartnerHash"]) {
      return (
        props.member["memberPartnerHash"] !==
        props.latestMemberSync["memberPartnerHash"]
      );
    }
    return props.member["memberHash"] !== props.latestMemberSync["memberHash"];
  };

  const isMemberSyncInProgress = () => {
    if (!props.isSyncReady) return false;
    if (!props.latestMemberSync) return false;
    return props.latestMemberSync.status === STARTED;
  };


  const clientStatusType = props.client ? props.client.clientStatusType : null;
  const syncType = props.client ? props.client.syncType : null;
  let clientBillingPeriodType = props.client
    ? props.client.clientBillingPeriodType
    : null;
  let memberDesyncStatus = isMemberDesynced();
  const memberSyncInProgress = isMemberSyncInProgress();

  const externalCoverageWebsite = !UrlUtils.isExternalUrl(
    client.coverageWebsite || ""
  )
    ? `http://${client.coverageWebsite}`
    : client.coverageWebsite;
  const customEmailAddress = props.member.customEmailAddress || "";
  const customPhoneNumber = props.member.customPhoneNumber || "";
  const customInformation = props.member.customInformation || "";

  const editData = {
    memberId,
    customEmailAddress,
    customPhoneNumber,
    customInformation,
  };
  const dependentCount = props.member.dependentPersons.filter(
    (dependentPerson) => !dependentPerson.isArchived
  ).length;

  const buildCoverageDescription = (memberName) => {
    const userLanguage = localStorage.getItem("languageCode") || "en-CA";

    if (userLanguage == FR.value) {
      return (
        frenchTranslations.downloader.coverageConfirmation + ": " + memberName
      );
    }

    return (
      englishTranslations.downloader.coverageConfirmation + ": " + memberName
    );
  };

  const getMemberCoverage = (coverages) => {
    let relevantCoverage = null;
    const currentDate = new Date(
      new Date().toLocaleString("en-US", {
        timeZone: "UTC",
      })
    );
    if (!coverages) return null;

    coverages.forEach((coverage) => {
      const startDate = new Date(
        new Date(coverage.startDate).toLocaleString("en-US", {
          timeZone: "UTC",
        })
      );
      let endDate = new Date(
        new Date(coverage.endDate).toLocaleString("en-US", {
          timeZone: "UTC",
        })
      );
      endDate = new Date(endDate.setDate(endDate.getDate() + 1));

      if (
        (startDate <= currentDate &&
          (!coverage.endDate || endDate > currentDate)) ||
        (startDate > currentDate && !coverage.isInactive)
      ) {
        relevantCoverage = coverage;
        return;
      }
    });
    return relevantCoverage;
  };

  const buildMajorCardDescription = (memberName) => {
    const userLanguage = localStorage.getItem("languageCode") || "en-CA";

    if (userLanguage == "fr-CA") {
      return frenchTranslations.downloader.memberMajorCard + ": " + memberName;
    }
    return englishTranslations.downloader.memberMajorCard + ": " + memberName;
  };

  const relevantCoverage = getMemberCoverage(props.activeMemberCoverages);
  const canEditMember = canEditMemberByClientStatusType();
  const archiveButton = !props.member.isArchived ? {
    component: (
      <ArchiveButton
        showDefaultDanger={true}
        isArchived={isArchived}
        archivedName={`${firstName} ${lastName}`}
        archiveFunction={props.archiveMember.bind(null, memberId, isArchived)}
        label={
          isArchived ? (
            <FormattedMessage id="common.unarchive" />
          ) : (
            <FormattedMessage id="common.archive" />
          )
        }
      />
    ),
    icon: <span className="icon icon-bin" />,
    label: isArchived ? "common.unarchive" : "common.archive",
    requirement: canEditMember,
  } : undefined;

 const renderActivationStatus = () => {
  const { activationStatus } = props;

  if (activationStatus === null || activationStatus === undefined) {
    return <RectangleSpinner className="spinner-rect-inline" />;
  }

  const isVerified = activationStatus === EMAIL_VERIFIED;
  const statusClass = `status status-indicator status-${isVerified ? "verified" : "not-verified"}`;
  const statusKey = isVerified ? "status.emailVerified" : "status.emailNotVerified";

  return (
    <div className={statusClass}>
      <span>
        <FormattedMessage id={statusKey} />
      </span>
    </div>
  );
};
  const getMessageBox = () => {
    let messageBox = undefined;
    let memberCopy = JSON.parse(JSON.stringify(props.member));
    memberCopy.memberCoverages = props.allMemberCoverages;
    if (memberDesyncStatus || memberSyncInProgress) {
      if (!isMemberEligibleForSync(memberCopy, memberCopy.client) && !isArchivedEventError(props.latestMemberSync)) {
        messageBox = (
          <MessageBox className={`member-synchronization-alert alert-warning-no-sync`}>
            <FormattedMessage id="members.nonMemberSync" />
          </MessageBox>
        );

        return (
          <OverlayWithToolTip
            placement="right"
            toolTipId="members.datatable.toolTips.memberNoSync"
            customComponent={messageBox} />
        );
      } else {
        messageBox = (<MessageBox key="memberSyncWarning" className={memberDesyncStatus ? "transition-alert" : "transition-info"}>
          <FormattedMessage id={memberDesyncStatus ? "members.memberNotSynced" : "members.datatable.toolTips.syncToolTipStarted"} />
        </MessageBox>
        );
        return getOverlayForSync(
          { member: props.member, client: props.member.client },
          props.latestMemberSync,
          "members.datatable",
          "memberPartnerHash",
          "memberHash",
          "member",
          true,
          false,
          messageBox,
          "right"
        );
      }
    }
  };

  return (
    <div className="page-header">
      <div className="main clearfix">
        <div className="main-name">
          <h2 className="name">
            {firstName} {lastName}
          </h2>
        </div>

        <div className="member-details">
          <span className="name">
            <FormattedMessage id="memberCoverages.form.occupation" /> :{" "}
            {props.member.memberCoverages[0] != null
              ? [
                props.member.memberCoverages[0].occupation != ""
                  ? props.member.memberCoverages[0].occupation
                  : "N/D",
              ]
              : "N/D"}
          </span>
          <br />
          <span className="number">
            <Link to={`/dashboard/clients/${client.clientId}`}>
              {client.name}
            </Link>{" "}
            / {memberIdentifier}
          </span>
          <br />
        </div>
        <div className="new-contact">
          <div className="name-title">
            {(client.adminContactFirstName || client.adminContactLastName) && (
              <h3 className="name">
                {client.adminContactFirstName} {client.adminContactLastName}
              </h3>
            )}
            {client.adminContactJobPosition && (
              <span className="title">{client.adminContactJobPosition}</span>
            )}
          </div>
          <div className="new-details">
            {client.adminContactPhoneNumber && (
              <span className="detail">{client.adminContactPhoneNumber}</span>
            )}

            {client.adminContactEmailAddress && (
              <span className="detail">{client.adminContactEmailAddress}</span>
            )}
            {client.coverageWebsite && (
              <span className="detail">
                <a target="_blank" href={externalCoverageWebsite}>
                  {client.cleanCoverageWebsite}
                </a>
              </span>
            )}
          </div>
        </div>
        {renderMemberHeaderWarnings()}
        <div>
          {clientInTransition && (
            <MessageBox key="clientNumberWarning" className="transition-alert">
              <span className="d-flex">
                <FormattedMessage id="importExport.importMembers.form.warnings.clientInTransition" />
                <button
                  className="refresh-transition"
                  type="button"
                  onClick={() => props.refetchIsTransition()}
                >
                  <span className="icon icon-loop" />
                </button>
              </span>
            </MessageBox>
          )}
          {canViewSyncStatus(props.client, MEMBER) && getMessageBox()}
          {props.isMintEnabled && props.activationStatus && (
            <span className="activation-status">
              {renderActivationStatus()}
            </span>
          )}
        {props.isMintEnabled && props.activationStatus && props.member.completeButNotActivated &&  !props.member.isArchived && (
        <span className="activation-status">
          <div className="status status-indicator status-not-verified">
            <OverlayWithToolTip
              placement="top"
              toolTipId="members.completeButNotActivatedWarning"
              customComponent={<span className="icon icon-warning" />}
            />
          </div>
        </span>
      )}
              </div>
        <div className="sub-nav">
          <ul>
            <li>
              <IndexLink
                to={`/dashboard/clients/${client.clientId}/members/${memberId}/profile`}
                activeClassName="active"
              >
                <FormattedMessage id="members.member.pages.profile" />
              </IndexLink>
            </li>
            {client.clientBillingPeriodType === RECURRING.value && <li>
              <Link
                to={`/dashboard/clients/${client.clientId}/members/${memberId}/employments`}
                activeClassName="active"
              >
                <FormattedMessage
                  id="members.member.pages.employments"
                />
              </Link>
            </li>}
            {canViewMemberCoverageByClientStatusType() && (
              <li>
                <Link
                  to={`/dashboard/clients/${client.clientId}/members/${memberId}/coverage`}
                  activeClassName="active"
                >
                  <FormattedMessage id="members.member.pages.insurance" />
                </Link>
              </li>
            )}
            <li>
              <Link
                to={`/dashboard/clients/${client.clientId}/members/${memberId}/dependents`}
                activeClassName="active"
              >
                <FormattedMessage
                  id="members.member.pages.dependents"
                  values={{ count: dependentCount }}
                />
              </Link>
            </li>
            <li>
              <Link
                to={`/dashboard/clients/${client.clientId}/members/${memberId}/beneficiaries`}
                activeClassName="active"
              >
                <FormattedMessage
                  id="members.member.pages.beneficiaries"
                  values={{ count: props.beneficiaryCount }}
                />
              </Link>
            </li>
          </ul>
        </div>
        <div className="header-right">
          <div className="header-right-account">
            <FormattedMessage id="clients.form.otherInformation.accountManager" />{" "}
            :{" "}
            {client.accountManagerId
              ? `${client.accountManager.firstName} ${client.accountManager.lastName}`
              : "N/D"}
            <br />
            {client.accountManagerId
              ? `${client.accountManager.emailAddress}`
              : ""}
            {client.accountManagerId &&
              client.accountManager.phone &&
              client.accountManager.emailAddress
              ? " | "
              : ""}
            {client.accountManagerId && client.accountManager.phone
              ? `${client.accountManager.phone}`
              : ""}
            <br />
          </div>
          <span className="nav-icons">
            {!!props.member.client.coverageWebsite ? (
              <a href={externalCoverageWebsite} target="_blank">
                <span className="icon-info" />
              </a>
            ) : null}
            {![INACTIVE.value, TERMINATED.value].includes(clientStatusType) &&
              props.member.memberCoverages[0] != null ? (
              [
                !!relevantCoverage &&
                  !relevantCoverage.isInactive &&
                  !isArchived &&
                  syncType != TED_DESJARDINS.value &&
                  clientBillingPeriodType != MANUAL.value ? (
                  <OptionsMenu
                    key={props.member.memberId}
                    options={[
                      {
                        component: (
                          <DownloadButton
                            model={MEMBERS}
                            path={`${memberId}/major-card`}
                            filters={[]}
                            skipModal={true}
                            identifier={buildMajorCardDescription(
                              props.member.firstName +
                              " " +
                              props.member.lastName
                            )}
                            disabled={
                              !canDownload(
                                buildMajorCardDescription(
                                  props.member.firstName +
                                  " " +
                                  props.member.lastName
                                ),
                                props.currentDownloads
                              )
                            }
                            toastMsg="members.member.actions.generating"
                            closeButton={false}
                            showGeneratingToast={true}
                          />
                        ),
                        icon: <span className="icon icon-in" />,
                        label: "members.member.actions.majorCard",
                      },
                      !isArchived && !userHasAccess(HR_CLERK, true) ? {
                        component: (
                          <DownloadButton
                            model={MEMBERS}
                            path={`${memberId}/coverage-confirmation`}
                            filters={[]}
                            identifier={buildCoverageDescription(
                              props.member.firstName +
                              " " +
                              props.member.lastName
                            )}
                            customFields={
                              <Field
                                id="memberCoverageId"
                                name="memberCoverageId"
                                component={SelectInput}
                                addBlankOption={true}
                                sortOptions={false}
                                options={props.activeMemberCoverages}
                                label={
                                  <FormattedMessage id="memberCoverages.datatable.columns.period" />
                                }
                              />
                            }
                            additionalValidation={{
                              memberCoverageId: {
                                isRequiredTrim: { value: true },
                              },
                            }}
                            alterParameters={(values, parameters) => ({
                              ...parameters,
                              path: `${parameters.path}?memberCoverageId=${values.memberCoverageId}`,
                            })}
                          />
                        ),
                        icon: <span className="icon icon-in" />,
                        label: "members.member.actions.confirmation",
                      } : undefined,
                      archiveButton,
                    ].filter(item => item !== undefined)}
                  />
                ) : !userHasAccess(HR_CLERK, true) && (
                  <OptionsMenu
                    key={props.member.memberId}
                    options={[
                      !isArchived ? {
                        component: (
                          <DownloadButton
                            model={MEMBERS}
                            path={`${memberId}/coverage-confirmation`}
                            filters={[]}
                            identifier={buildCoverageDescription(
                              props.member.firstName +
                              " " +
                              props.member.lastName
                            )}
                            customFields={
                              <Field
                                id="memberCoverageId"
                                name="memberCoverageId"
                                component={SelectInput}
                                addBlankOption={true}
                                sortOptions={false}
                                options={props.activeMemberCoverages}
                                label={
                                  <FormattedMessage id="memberCoverages.datatable.columns.period" />
                                }
                              />
                            }
                            additionalValidation={{
                              memberCoverageId: {
                                isRequiredTrim: { value: true },
                              },
                            }}
                            alterParameters={(values, parameters) => ({
                              ...parameters,
                              path: `${parameters.path}?memberCoverageId=${values.memberCoverageId}`,
                            })}
                          />
                        ),
                        icon: <span className="icon icon-in" />,
                        label: "members.member.actions.confirmation",
                      } : undefined,
                      archiveButton,
                    ].filter(item => item !== undefined)}
                  />
                ),
              ]
            ) : (
              <OptionsMenu
                key={props.member.memberId}
                options={
                  ![INACTIVE.value, TERMINATED.value].includes(clientStatusType)
                    ? [
                      !isArchived && !userHasAccess(HR_CLERK, true) ? {
                        component: (
                          <DownloadButton
                            model={MEMBERS}
                            path={`${memberId}/coverage-confirmation`}
                            filters={[]}
                            identifier={buildCoverageDescription(
                              props.member.firstName +
                              " " +
                              props.member.lastName
                            )}
                            customFields={
                              <Field
                                id="memberCoverageId"
                                name="memberCoverageId"
                                component={SelectInput}
                                addBlankOption={true}
                                sortOptions={false}
                                options={props.activeMemberCoverages}
                                label={
                                  <FormattedMessage id="memberCoverages.datatable.columns.period" />
                                }
                              />
                            }
                            additionalValidation={{
                              memberCoverageId: {
                                isRequiredTrim: { value: true },
                              },
                            }}
                            alterParameters={(values, parameters) => ({
                              ...parameters,
                              path: `${parameters.path}?memberCoverageId=${values.memberCoverageId}`,
                            })}
                          />
                        ),
                        icon: <span className="icon icon-in" />,
                        label: "members.member.actions.confirmation",
                      } : undefined,
                      archiveButton,
                    ].filter(item => item !== undefined)
                    : [archiveButton].filter(item => item !== undefined)
                }
              />
            )}
          </span>
          <div className="member-custom-contact">
            <div className="custom-email">
              {canEditMember && (
                <EditButton
                  className="member-custom-edit-button"
                  title={<FormattedMessage id="members.member.actions.edit" />}
                  form={BasicCustomContactFields}
                  data={editData}
                  editFunction={props.onSubmit}
                />
              )}
              {customEmailAddress}
            </div>
            <div className="custom-phone">{customPhoneNumber}</div>
            <div className="additional-information">{customInformation}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const currentDownloads = state.downloader || {};

  return {
    currentDownloads: currentDownloads.files || [],
  };
};

MemberHeader.propTypes = {
  activeMemberCoverages: PropTypes.array,
  authLanguageCode: PropTypes.string,
  path: PropTypes.string,
  member: PropTypes.object,
  client: PropTypes.object,
  companyId: PropTypes.string,
  archiveMember: PropTypes.func,
  onSubmit: PropTypes.func,
  beneficiaryCount: PropTypes.number,
  occupation: PropTypes.string,
  clientInTransition: PropTypes.bool,
  refetchIsTransition: PropTypes.func,
  isSyncReady: PropTypes.bool,
  latestMemberSync: PropTypes.object,
  activationStatus: PropTypes.object,
  allMemberCoverages: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  currentDownloads: PropTypes.array,
  isMintEnabled: PropTypes.bool
};

export default connect(mapStateToProps)(MemberHeader);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/members/member/MemberHeader.js